import { useEffect, useRef, useState } from "react";
import { Lightbulb, RefreshCw } from "lucide-react";
import { Spinner } from "./Brand";
import { NoteHeader } from "./AiNote";
import { api } from "@/lib/api";
import { needLabel } from "@/lib/schemaFlip";

// One or two gentle ideas for meeting each chosen need; parent keeps them to save with the entry.
export function NeedIdeas({ needs, context, value, onChange }) {
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const asked = useRef("");
  const labels = needs.filter((n) => n.includes(":")).map(needLabel);
  const sig = labels.join("|");

  const load = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const { data } = await api.post("/ai/need-ideas", { needs: labels.slice(0, 8), context }, { timeout: 60000 });
      onChange(data.ideas);
      setFailed(!data.ideas.length);
    } catch {
      setFailed(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!labels.length || asked.current === sig || (value?.length && value.every((v) => labels.includes(v.need)))) return;
    asked.current = sig;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  if (!labels.length) return null;
  const spoken = (value || []).map((v) => `For ${v.need.toLowerCase()}: ${v.ideas.join(" ")}`).join(" ");
  return (
    <div className="rounded-[20px] border border-t-border bg-t-surface2 p-5" data-testid="need-ideas">
      <NoteHeader title={<p className="flex items-center gap-2 font-semibold text-t-on"><Lightbulb size={17} className="text-t-brand" /> Gentle ideas for meeting your needs</p>} text={!loading && value?.length ? spoken : null} testId="need-ideas-listen" />
      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-t-on2"><Spinner className="w-4 h-4 text-t-brand" /> Finding a few small, kind ideas…</div>
      ) : value?.length ? (
        <>
          <div className="mt-3 flex flex-col gap-3">
            {value.map((v, i) => (
              <div key={v.need} className="rounded-2xl bg-t-surface border border-t-border p-4" data-testid={`need-idea-${i}`}>
                <p className="tf-tag">{v.need}</p>
                <ul className="mt-2 flex flex-col gap-1.5">
                  {v.ideas.map((idea) => <li key={idea} className="text-[15px] text-t-on leading-relaxed flex gap-2"><span className="text-t-brand">•</span>{idea}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button type="button" onClick={load} className="inline-flex items-center gap-1.5 text-sm font-semibold text-t-brand" data-testid="need-ideas-refresh"><RefreshCw size={14} /> Different ideas</button>
          </div>
          <p className="mt-2 text-xs text-t-muted">These are saved with this entry so you can come back to them in History.</p>
        </>
      ) : (
        <div className="mt-2 text-sm text-t-on2">
          {failed ? "Ideas couldn't load just now." : ""}
          <button type="button" onClick={load} className="ml-1 font-semibold text-t-brand" data-testid="need-ideas-retry">Get ideas</button>
        </div>
      )}
    </div>
  );
}

export function NeedIdeasList({ ideas }) {
  if (!ideas?.length) return null;
  return (
    <div className="flex flex-col gap-2" data-testid="saved-need-ideas">
      {ideas.map((v) => (
        <div key={v.need} className="rounded-xl bg-t-surface border border-t-border p-3">
          <p className="text-sm font-semibold text-t-on">{v.need}</p>
          {v.ideas.map((i) => <p key={i} className="text-sm text-t-on2">• {i}</p>)}
        </div>
      ))}
    </div>
  );
}
